<template>
  <div class="ops-views" role="tablist" aria-label="Practice Ops view" data-test="practice-ops-views">
    <template v-for="item in items">
      <button
        :key="item.id"
        type="button"
        role="tab"
        class="ops-views__tab"
        :class="{ 'ops-views__tab--on': view === item.id }"
        :aria-selected="view === item.id ? 'true' : 'false'"
        :data-test="`practice-ops-view-${item.id}`"
        @click="$emit('select', item.id)"
      >{{ item.label }}</button>
      <!-- The Wall's theme sits right after "Wall display": a small sun / moon switch, per device (viewPref.js wallTheme). -->
      <span
        v-if="showTheme && item.id === 'wall'"
        :key="`${item.id}-theme`"
        class="ops-theme"
        role="group"
        aria-label="Wall display theme"
        data-test="practice-ops-wall-theme"
      >
        <button
          v-for="theme in themeItems"
          :key="theme.id"
          type="button"
          class="ops-theme__btn"
          :class="{ 'ops-theme__btn--on': wallTheme === theme.id }"
          :aria-pressed="wallTheme === theme.id ? 'true' : 'false'"
          :aria-label="`${theme.label} theme`"
          :title="`${theme.label} theme`"
          :data-test="`practice-ops-theme-${theme.id}`"
          @click="$emit('theme', theme.id)"
        >
          <svg v-if="theme.id === 'light'" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" /></svg>
          <svg v-else viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" /></svg>
        </button>
      </span>
    </template>
  </div>
</template>

<script>
/**
 * The Practice Ops view picker (design: "Board · Wall display · Operatory · Phone" in one pill). Presentational: it emits, the PAGE
 * navigates / stores (PracticeOpsPage.setView / setWallTheme). Drawn in the page header AND in the Operatory's own top bar (which
 * has no page header), so there is ONE picker and one set of test hooks.
 *
 *   props  view       the open view id · items [{ id, label }] in the order drawn
 *          showTheme  draw the Light / Dark switch right after the "Wall display" tab (the page header only)
 *          wallTheme  'light' | 'dark'
 *   emits  select(id) · theme(id)
 *
 * ⛔ No prop is named `patNum`, `siteID`, `loading` or `allSettings` (mixin.js computeds shadow them).
 */
const THEME_ITEMS = [
  { id: 'light', label: 'Light' },
  { id: 'dark', label: 'Dark' },
];

export default {
  name: 'OpsViewPicker',

  props: {
    view: { type: String, default: 'board' },
    items: { type: Array, required: true },
    showTheme: { type: Boolean, default: false },
    wallTheme: { type: String, default: 'light' },
  },

  data() {
    return { themeItems: THEME_ITEMS };
  },
};
</script>

<style lang="scss" scoped>
.ops-views {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border-radius: 10px;
  background: #eff3f2;
}

.ops-views__tab {
  height: 30px;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #3a4240;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  &:focus-visible { outline: 2px solid #4fa98d; outline-offset: 1px; }
}

.ops-views__tab--on {
  background: #ffffff;
  color: #0f7259;
  box-shadow: 0 1px 2px rgba(12, 15, 14, 0.1);
}

// the compact sun / moon switch: a small segmented control inside the pill
.ops-theme {
  display: inline-flex;
  align-items: center;
  gap: 1px;
  margin: 0 4px 0 -2px;
  padding: 2px;
  border-radius: 8px;
  background: #e1e7e5;
}

.ops-theme__btn {
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #3a4240;
  cursor: pointer;

  svg { display: block; }
  &:focus-visible { outline: 2px solid #4fa98d; outline-offset: 1px; }
}

.ops-theme__btn--on {
  background: #ffffff;
  color: #0f7259;
  box-shadow: 0 1px 2px rgba(12, 15, 14, 0.1);
}
</style>
